import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Spinner from '../../components/Spinner'
import { getOrder, cancelOrder } from '../../api/orders'
import { getSettings } from '../../api/settings'
import { formatMoney, formatDate, orderStatusLabel, orderStatusColor } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useTelegram } from '../../hooks/useTelegram'
import { Payment } from '../../types'

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const S2 = 'rgba(255,248,235,0.08)'
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

const CANCEL_REASONS: Record<string, string[]> = {
  ru: ['Передумал', 'Нашёл дешевле', 'Нет возможности платить', 'Слишком долго рассматривают', 'Другое'],
  uz: ["Fikrimni o'zgartirdim", 'Arzonroqini topdim', "To'lay olmayman", "Ko'rib chiqish uzoq davom etyapti", 'Boshqa sabab'],
}

const PAY_STATUS: Record<string, { label_ru: string; label_uz: string; color: string }> = {
  pending:              { label_ru: 'Ожидает',     label_uz: 'Kutilmoqda',    color: TX3 },
  awaiting_confirmation:{ label_ru: 'На проверке', label_uz: 'Tekshirilmoqda', color: '#8A9DC0' },
  paid:                 { label_ru: 'Оплачен',     label_uz: "To'landi",       color: '#4CAF7E' },
  overdue:              { label_ru: 'Просрочен',   label_uz: 'Kechikkan',      color: '#E05E5E' },
  partially_paid:       { label_ru: 'Частично',    label_uz: 'Qisman',         color: G },
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 6, color: TX3, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
    </button>
  )
}

function PaymentRow({ payment, onPay, isFirstUnpaid, rate, lang }: {
  payment: Payment; onPay?: (p: Payment) => void; isFirstUnpaid: boolean; rate: number; lang: string
}) {
  const st = PAY_STATUS[payment.status] ?? PAY_STATUS.pending
  const canPay = !!onPay && isFirstUnpaid && (payment.status === 'pending' || payment.status === 'overdue' || payment.status === 'partially_paid')
  const hint = `≈ ${Math.round(payment.amount * rate).toLocaleString('ru-RU')} ${lang === 'uz' ? "so'm" : 'сум'}`

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 14px', borderRadius: 12, marginBottom: 6,
      background: payment.status === 'paid' ? 'rgba(76,175,126,0.06)' : payment.status === 'overdue' ? 'rgba(224,94,94,0.06)' : S,
      border: `1px solid ${payment.status === 'paid' ? 'rgba(76,175,126,0.15)' : payment.status === 'overdue' ? 'rgba(224,94,94,0.15)' : BD}`,
    }}>
      <div>
        <p style={{ margin: 0, fontSize: 12, color: TX3 }}>
          #{payment.payment_num} · {formatDate(payment.due_date)}
        </p>
        <p style={{ margin: '3px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: TX }}>
          {formatMoney(payment.amount)}
        </p>
        {payment.status === 'partially_paid' && (
          <p style={{ margin: '2px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: G }}>
            {lang === 'uz' ? `To'langan: ${formatMoney(payment.paid_amount)}` : `Оплачено: ${formatMoney(payment.paid_amount)}`}
          </p>
        )}
        <p style={{ margin: '2px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>{hint}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: st.color, letterSpacing: '0.04em' }}>
          {lang === 'uz' ? st.label_uz : st.label_ru}
        </span>
        {canPay && (
          <button
            onClick={() => onPay!(payment)}
            style={{
              padding: '5px 12px', borderRadius: 99,
              background: GB, border: `1px solid ${G}`,
              color: G, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {lang === 'uz' ? "To'lash" : 'Оплатить'}
          </button>
        )}
        {payment.status === 'awaiting_confirmation' && (
          <span style={{ fontSize: 10, color: '#8A9DC0', letterSpacing: '0.02em' }}>⏳</span>
        )}
        {payment.status === 'paid' && payment.paid_at && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#4CAF7E' }}>
            {formatDate(payment.paid_at)}
          </span>
        )}
      </div>
    </div>
  )
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const lang = user?.language ?? 'ru'
  const qc = useQueryClient()

  const [showCancelSheet, setShowCancelSheet] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(Number(id)),
    enabled: !!id,
  })

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 30_000 })
  const usdRate = settings?.usd_rate ?? 12000

  const cancelMut = useMutation({
    mutationFn: (reason: string) => cancelOrder(Number(id), reason),
    onSuccess: () => {
      toast.success(lang === 'uz' ? 'Ariza bekor qilindi' : 'Заявка отменена')
      tg?.HapticFeedback.notificationOccurred('warning')
      setShowCancelSheet(false)
      qc.invalidateQueries({ queryKey: ['order', id] })
      qc.invalidateQueries({ queryKey: ['my-orders'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? (lang === 'uz' ? 'Xatolik' : 'Ошибка')),
  })

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  if (isLoading) return <Spinner className="h-screen" />
  if (!order) return null

  const productName = order.product
    ? (lang === 'uz' ? order.product.name_uz : order.product.name_ru)
    : (lang === 'uz' ? "Maxsus ariza" : 'Произвольная заявка')
  const paidCount  = order.payments?.filter(p => p.status === 'paid').length ?? 0
  const totalCount = order.payments?.length ?? order.months
  const paidAmount = order.payments?.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0) ?? 0
  const remainAmt  = order.total_amount - paidAmount
  const progress   = totalCount > 0 ? paidCount / totalCount : 0

  const firstUnpaidNum = order.payments
    ?.filter(p => p.status === 'pending' || p.status === 'overdue' || p.status === 'partially_paid')
    .sort((a, b) => a.payment_num - b.payment_num)[0]?.payment_num ?? null

  function handlePay(payment: Payment) {
    navigate(`/orders/${order!.id}/pay/${payment.id}`)
  }

  // Status badge color
  const statusMap: Record<string, { label_ru: string; label_uz: string; color: string; bg: string }> = {
    pending:   { label_ru: 'На рассмотрении', label_uz: "Ko'rib chiqilmoqda", color: '#8A9DC0', bg: 'rgba(138,157,192,0.12)' },
    reviewing: { label_ru: 'Проверка',        label_uz: "Ko'rib chiqish",     color: '#8A9DC0', bg: 'rgba(138,157,192,0.12)' },
    approved:  { label_ru: 'Одобрена',         label_uz: 'Tasdiqlangan',       color: G,          bg: GB },
    active:    { label_ru: 'Активна',          label_uz: 'Faol',               color: '#4CAF7E',  bg: 'rgba(76,175,126,0.12)' },
    closed:    { label_ru: 'Закрыта',          label_uz: 'Yopilgan',           color: TX3,        bg: 'rgba(138,132,128,0.10)' },
    rejected:  { label_ru: 'Отказ',            label_uz: 'Rad etilgan',        color: '#E05E5E',  bg: 'rgba(224,94,94,0.10)' },
    cancelled: { label_ru: 'Отменена',         label_uz: 'Bekor qilindi',      color: TX3,        bg: 'rgba(138,132,128,0.10)' },
  }
  const st = statusMap[order.status] ?? statusMap.pending

  const rows: [string, string, boolean?][] = [
    [lang === 'uz' ? 'Narx' : 'Цена',                          formatMoney(order.price_after_discount)],
    [lang === 'uz' ? "Boshlang'ich to'lov" : 'Первый взнос',    formatMoney(order.down_payment)],
    [lang === 'uz' ? 'Muddat' : 'Срок',                         `${order.months} ${lang === 'uz' ? 'oy' : 'мес.'}`],
    [lang === 'uz' ? 'Stavka' : 'Ставка',                       `${order.interest_rate}%`],
    [lang === 'uz' ? 'Oylik' : 'В месяц',                       formatMoney(order.monthly_amount), true],
    [lang === 'uz' ? 'Jami' : 'Итого',                          formatMoney(order.total_amount)],
    [lang === 'uz' ? 'Topshirildi' : 'Подана',                  formatDate(order.submitted_at)],
    ...(order.approved_at ? [[lang === 'uz' ? 'Tasdiqlandi' : 'Одобрена', formatDate(order.approved_at)] as [string,string]] : []),
    ...(order.activated_at ? [[lang === 'uz' ? 'Faollashtirildi' : 'Активирована', formatDate(order.activated_at)] as [string,string]] : []),
  ]

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <BackButton onClick={() => navigate(-1)} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
          {order.registry_num}
        </span>
      </header>

      <main style={{ flex: 1, padding: '20px 20px 40px', overflowY: 'auto' }}>
        {/* Title + status */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 26, color: TX, margin: '0 0 10px', lineHeight: 1.15 }}>
            {productName}
          </h1>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
            color: st.color, background: st.bg,
          }}>
            {lang === 'uz' ? st.label_uz : st.label_ru}
          </span>
        </div>

        {/* Progress card (active) */}
        {order.status === 'active' && (
          <div style={{ padding: 18, borderRadius: 16, ...GLASS, border: `1px solid ${BD}`, marginBottom: 18 }}>
            <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {lang === 'uz' ? 'PROGRESS' : 'ПРОГРЕСС'}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, color: TX }}>
                {formatMoney(order.monthly_amount)}
              </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
                {paidCount} / {totalCount} {lang === 'uz' ? "to'lov" : 'платеж'}
              </span>
            </div>
            <div style={{ height: 4, background: 'rgba(244,238,225,0.08)', borderRadius: 99 }}>
              <div style={{ height: '100%', width: `${progress * 100}%`, background: '#4CAF7E', borderRadius: 99 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>
                {formatMoney(paidAmount)} {lang === 'uz' ? "to'langan" : 'оплачено'}
              </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3 }}>
                {formatMoney(remainAmt)} {lang === 'uz' ? 'qoldi' : 'осталось'}
              </span>
            </div>
          </div>
        )}

        {/* Manager note */}
        {order.manager_note && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(234,179,8,0.09)', border: '1px solid rgba(234,179,8,0.25)', marginBottom: 18 }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#EAB308', marginBottom: 5 }}>
              💬 {lang === 'uz' ? 'Menejer izohi' : 'Комментарий менеджера'}
            </div>
            <p style={{ fontSize: 13, color: '#F5F4F0', margin: 0 }}>{order.manager_note}</p>
          </div>
        )}

        {/* Reject reason */}
        {order.reject_reason && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(224,94,94,0.08)', border: '1px solid rgba(224,94,94,0.20)', marginBottom: 18 }}>
            <p style={{ fontSize: 13, color: '#E05E5E', margin: 0 }}>
              {lang === 'uz' ? 'Rad etish sababi' : 'Причина отказа'}: {order.reject_reason}
            </p>
          </div>
        )}

        {/* Cancel reason */}
        {order.status === 'cancelled' && order.cancel_reason && (
          <div style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(138,132,128,0.08)', border: `1px solid ${BD}`, marginBottom: 18 }}>
            <p style={{ fontSize: 11, color: TX3, marginBottom: 4 }}>
              {order.cancelled_by_role === 'admin'
                ? (lang === 'uz' ? 'Admin tomonidan bekor qilindi' : 'Отменено администратором')
                : (lang === 'uz' ? 'Siz bekor qildingiz' : 'Вы отменили заявку')}
            </p>
            <p style={{ fontSize: 13, color: TX2, margin: 0 }}>{order.cancel_reason}</p>
          </div>
        )}

        {/* Order conditions */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12 }}>
            {lang === 'uz' ? 'SHARTLAR' : 'УСЛОВИЯ'}
          </p>
          {rows.map(([label, value, hi], i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '11px 0',
              borderBottom: i < rows.length - 1 ? `1px solid ${BD}` : 'none',
            }}>
              <span style={{ fontSize: 13, color: TX2 }}>{label}</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: hi ? 15 : 13,
                color: hi ? G : TX,
                fontWeight: hi ? 600 : 400,
              }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Payment schedule */}
        {order.payments && order.payments.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 12 }}>
              {lang === 'uz' ? "TO'LOV JADVALI" : 'ГРАФИК ПЛАТЕЖЕЙ'}
            </p>
            {order.payments.map(p => (
              <PaymentRow
                key={p.id}
                payment={p}
                onPay={order.status === 'active' ? handlePay : undefined}
                isFirstUnpaid={p.payment_num === firstUnpaidNum}
                rate={usdRate}
                lang={lang}
              />
            ))}
          </div>
        )}

        {/* Cancel button */}
        {['pending', 'reviewing', 'approved'].includes(order.status) && (
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => { setCancelReason(''); setShowCancelSheet(true) }}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 14,
                background: 'rgba(224,94,94,0.06)', border: '1px solid rgba(224,94,94,0.20)',
                color: '#E05E5E', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              {lang === 'uz' ? 'Arizani bekor qilish' : 'Отменить заявку'}
            </button>
          </div>
        )}
      </main>

      {/* Cancel bottom sheet */}
      {showCancelSheet && (
        <div
          onClick={() => setShowCancelSheet(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 480, borderRadius: '20px 20px 0 0',
              background: '#162038', border: `1px solid ${BD}`, padding: '16px 20px 40px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 99, background: BD }} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: TX, margin: '0 0 16px' }}>
              {lang === 'uz' ? 'Bekor qilish sababi' : 'Причина отмены'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {CANCEL_REASONS[lang === 'uz' ? 'uz' : 'ru'].map(reason => (
                <button
                  key={reason}
                  onClick={() => setCancelReason(reason)}
                  style={{
                    padding: '12px 14px', borderRadius: 12, textAlign: 'left', fontSize: 14,
                    background: cancelReason === reason ? 'rgba(224,94,94,0.10)' : S,
                    border: cancelReason === reason ? '1px solid rgba(224,94,94,0.30)' : `1px solid ${BD}`,
                    color: cancelReason === reason ? '#E05E5E' : TX,
                    cursor: 'pointer',
                  }}
                >
                  {reason}
                </button>
              ))}
            </div>
            <button
              onClick={() => cancelReason && cancelMut.mutate(cancelReason)}
              disabled={!cancelReason || cancelMut.isPending}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 15, fontWeight: 600,
                background: cancelReason ? 'rgba(224,94,94,0.12)' : 'rgba(244,238,225,0.04)',
                border: cancelReason ? '1px solid rgba(224,94,94,0.35)' : `1px solid ${BD}`,
                color: cancelReason ? '#E05E5E' : TX3,
                cursor: cancelReason ? 'pointer' : 'default',
                opacity: cancelMut.isPending ? 0.5 : 1,
              }}
            >
              {cancelMut.isPending ? '...' : (lang === 'uz' ? 'Bekor qilish' : 'Отменить заявку')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
